/**
 * Which screen the URL is asking for.
 *
 * Pure: a string in, a screen out. No history calls, no side effects — so the
 * rules live somewhere they can be tested by calling a function, and app.js
 * only has to subscribe to `hashchange` and render what this says.
 *
 * One `id` field, never a per-screen name for it. A match's id lived in
 * `liveId` while everything else used `id`, which meant path(parse(x)) was not
 * x for exactly one screen — two names for one fact, which this file's own
 * rules say drift.
 *
 * The hash, rather than real paths, on purpose. A path like /match/123 needs a
 * server catch-all AND a service-worker navigation fallback, because offline
 * that URL has never been fetched and never been cached — so the one thing
 * this app must survive, a cellar with no signal, is exactly where clean URLs
 * would hand somebody a blank screen. A hash never issues a navigation request
 * at all: the document is always '/', which is always cached. Installed, the
 * address bar is not on screen anyway, so the ugliness costs nothing.
 */

/** Screen name (what the components already call themselves) -> URL segment. */
const SEGMENT = {
  record: 'record',
  h2h: 'h2h',
  board: 'leaderboard',
  progress: 'progress',
  circle: 'circle',
  settings: 'settings',
  notifications: 'notifications',
  support: 'support',
  admin: 'admin',
  venue: 'venues',
  matches: 'matches',
  watch: 'watch',
  rules: 'rules',
  leagues: 'leagues',
  league: 'league',
};

const SCREEN = Object.fromEntries(
  Object.entries(SEGMENT).map(([screen, seg]) => [seg, screen]));

const HOME = { screen: 'home', id: null, view: null };

/** The screen a hash names. Anything unrecognised is home, never an error. */
export function parse(hash) {
  const trail = String(hash || '').replace(/^#/, '').replace(/^\//, '');
  if (!trail) return HOME;
  const [head, ...rest] = trail.split('/');
  // A match carries its id, so a live match is linkable and survives a reload.
  // A watch link carries its own authorisation and no session, so it is its
  // own screen rather than a mode of the live one.
  if (head === 'watch' && rest.length) {
    return { screen: 'watch', id: null, view: null,
             watchToken: decodeURIComponent(rest.join('/')) };
  }
  // Killer is a game, not a match — several people and no score — so it has
  // its own segment rather than a mode of one that would have to be guarded
  // for everywhere a match is read.
  // The QR on the pub wall. Its own screen, like a watch link, because it
  // carries its own authorisation and is arrived at from a camera.
  if (head === 'queue' && rest.length) {
    return { screen: 'joinqueue', id: null, view: null,
             queueToken: decodeURIComponent(rest.join('/')) };
  }
  if (head === 'knockout' && rest.length) {
    return { screen: 'knockout', id: decodeURIComponent(rest.join('/')),
             view: null };
  }
  if (head === 'killer' && rest.length) {
    return { screen: 'killer', id: decodeURIComponent(rest.join('/')),
             view: null };
  }
  if (head === 'match' && rest.length) {
    return { screen: 'live', id: decodeURIComponent(rest.join('/')),
             view: null };
  }
  // A fixture is a place inside a league, not a screen of its own — but it
  // needs TWO ids, and the generic `{screen}/{id}/{view}` shape below only
  // has room for one before it. `view` carries the fixture id here rather
  // than a third field, so `go('fixture', leagueId, fixtureId)` still fits
  // the same three-argument call every other screen uses.
  if (head === 'league' && rest.length >= 3 && rest[1] === 'fixture') {
    return { screen: 'fixture', id: decodeURIComponent(rest[0]),
             view: decodeURIComponent(rest[2]) };
  }
  // A player's page inside a league. Same shape as the fixture case just
  // above, for the same reason: TWO ids, and `view` carries the second
  // (the player key) rather than a third field, so `go('player', leagueId,
  // playerKey)` still fits the same three-argument call every other screen
  // uses.
  if (head === 'league' && rest.length >= 3 && rest[1] === 'player') {
    return { screen: 'player', id: decodeURIComponent(rest[0]),
             view: decodeURIComponent(rest[2]) };
  }
  // A team's page inside a league. Same shape as the fixture and player
  // cases just above, for the same reason: TWO ids, and `view` carries the
  // second (the circle id) rather than a third field, so `go('team',
  // leagueId, circleId)` still fits the same three-argument call every
  // other screen uses.
  if (head === 'league' && rest.length >= 3 && rest[1] === 'team') {
    return { screen: 'team', id: decodeURIComponent(rest[0]),
             view: decodeURIComponent(rest[2]) };
  }
  // One competition inside a league (issue #30). The odd one out: a cup is
  // named by TWO things -- which season, and which competition -- because
  // the same cup runs every year and "Captains Cup" alone names a dozen
  // different knockouts. So this is the one route that needs a fourth
  // field, `sub`, rather than squeezing a second id into `view` the way the
  // three cases above do. Everything else still reads {screen, id, view}
  // and never sees it.
  if (head === 'league' && rest.length >= 4 && rest[1] === 'cup') {
    return { screen: 'cup', id: decodeURIComponent(rest[0]),
             view: decodeURIComponent(rest[2]),
             sub: decodeURIComponent(rest[3]) };
  }
  if (!SCREEN[head]) return HOME;
  // Any screen may name a thing: one circle out of the four you are in, one
  // venue out of the list. Without it a detail view is a mode of a tab, which
  // cannot be linked to, reloaded, or reached with the back button.
  // A thing, and optionally a view OF that thing: #/circle/abc/manage. Two
  // segments rather than a screen per view, because "manage this circle" is
  // the same place seen differently, and a URL should say so.
  return {
    screen: SCREEN[head],
    id: rest.length ? decodeURIComponent(rest[0]) : null,
    view: rest.length > 1 ? decodeURIComponent(rest[1]) : null,
  };
}

/** The hash for a screen. The inverse of parse, for everything parse returns. */
export function path(screen, id = null, view = null, sub = null) {
  // A match's segment is not its screen name, so it keeps its own shape.
  if (screen === 'live' && id) return `#/match/${encodeURIComponent(id)}`;
  if (screen === 'killer' && id) return `#/killer/${encodeURIComponent(id)}`;
  if (screen === 'knockout' && id)
    return `#/knockout/${encodeURIComponent(id)}`;
  // The inverse of the parse() special case above: `id` is the league,
  // `view` is the fixture — carried in the same slot the generic path below
  // uses for a screen's optional second segment.
  if (screen === 'fixture' && id && view) {
    return `#/league/${encodeURIComponent(id)}`
      + `/fixture/${encodeURIComponent(view)}`;
  }
  // The inverse of the cup case: `id` is the league, `view` the season and
  // `sub` the competition. The only path() that takes a fourth argument,
  // for the reason parse() gives -- a cup needs its season to be one cup.
  if (screen === 'cup' && id && view && sub) {
    return `#/league/${encodeURIComponent(id)}/cup/`
      + `${encodeURIComponent(view)}/${encodeURIComponent(sub)}`;
  }
  // The inverse of the parse() special case above: `id` is the league,
  // `view` is the player key.
  if (screen === 'player' && id && view) {
    return `#/league/${encodeURIComponent(id)}`
      + `/player/${encodeURIComponent(view)}`;
  }
  // The inverse of the parse() special case above: `id` is the league,
  // `view` is the circle id.
  if (screen === 'team' && id && view) {
    return `#/league/${encodeURIComponent(id)}`
      + `/team/${encodeURIComponent(view)}`;
  }
  if (!SEGMENT[screen]) return '#/';
  if (!id) return `#/${SEGMENT[screen]}`;
  const at = `#/${SEGMENT[screen]}/${encodeURIComponent(id)}`;
  return view ? `${at}/${encodeURIComponent(view)}` : at;
}


/**
 * A short, stable name for the screen you are looking at.
 *
 * So a screen can be REFERRED TO. "The venue page" is four screens now, and
 * describing which one costs a paragraph and a screenshot; `VENUE/INFO` costs
 * two words and cannot be misread.
 *
 * DERIVED from the route, not a numbered registry. A registry has to be kept
 * in step by hand, and the moment it is not the numbers are worse than
 * nothing -- they point somewhere real and wrong. Deriving means a screen
 * added tomorrow is labelled tomorrow, without anybody remembering to.
 *
 * It is not the URL: a URL carries ids ("#/league/808fec5a-.../cup/Spring%20
 * 2026/Division%203%2F4...") and the whole point here is something a person
 * can say out loud.
 */
const TAG = {
  home: 'HOME', record: 'REC', board: 'BOARD', h2h: 'H2H',
  progress: 'PROG', circle: 'CIRCLE', settings: 'SET', admin: 'ADMIN',
  notifications: 'BELL',
  venue: 'VENUE', matches: 'MATCH', watch: 'WATCH', rules: 'RULES',
  leagues: 'LEAGUES', league: 'LEAGUE', fixture: 'FIXTURE',
  player: 'PLAYER', team: 'TEAM', cup: 'CUP', live: 'LIVE',
  killer: 'KILLER', knockout: 'KO', joinqueue: 'QUEUE',
};

// Views worth naming. A view not here falls through to its own name upper
// -cased, so a new room is labelled the day it exists rather than the day
// somebody remembers to add it.
const VIEW_TAG = {
  manage: 'MANAGE', circles: 'CIRCLES', info: 'INFO', board: 'BOARD',
  chat: 'CHAT', home: 'HOME', fixtures: 'HUB', cups: 'CUPS',
  table: 'STANDINGS', stats: 'STATS', venues: 'VENUES',
  committee: 'CTTEE', search: 'SEARCH', calendar: 'CAL',
};

// What a screen opens on when the address names no view. Without these the
// bare `#/venues/{id}` tags as "VENUE" while its three siblings tag as
// "VENUE/CIRCLES" and "VENUE/MANAGE" -- so the one screen somebody is most
// likely to be looking at is the one with the vaguest name, which is the
// opposite of the point.
//
// The only hand-kept part of this, and small enough to stay true. A test
// pins every entry, so a default that changes fails rather than drifts.
const DEFAULT_VIEW = { venue: 'info', league: 'home', circle: 'board' };

export function pageTag(route) {
  const { screen, view } = route || {};
  const head = TAG[screen] || String(screen || 'HOME').toUpperCase();
  // The second segment is an id on these -- a fixture, a player, a team, a
  // cup -- and an id is exactly what a tag must not contain.
  if (['fixture', 'player', 'team', 'cup', 'live', 'killer',
       'knockout'].includes(screen)) return head;
  const on = view || DEFAULT_VIEW[screen];
  if (!on) return head;
  return `${head}/${VIEW_TAG[on] || String(on).toUpperCase()}`;
}
